import React from "react"

import { useTranslation } from "react-i18next"

import {Modal} from "components/modals/Modal"

import { URL } from "constants/api/requests"

import { AdminPlayerBonus2 } from "services/bonus/type"

import { formatDateTime, formatFiatCurrency } from "util/formatter"

import { getDisplayName, isExpired,isExpiringSoon } from "../../helpers/bonus2.helpers"
import { formatStake } from "../../helpers/spin-stake.helpers"

interface FreeSpinsDetailModalProps {
  target: AdminPlayerBonus2
  onClose: () => void
}

export const FreeSpinsDetailModal: React.FC<FreeSpinsDetailModalProps> = ({ target, onClose }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  return (
    <Modal open={true} handler={onClose}>
      <div className="modal-box w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold dark:text-white">{t("bonus2.spins.freeSpinsDetails")}</h3>
          <button onClick={onClose} className="btn btn-sm btn-circle btn-ghost">
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-base-200 dark:bg-[#0C0C1A] rounded-lg p-4">
            <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">{t("bonus2.spins.player")}</div>
            <a
              href={URL.USER.replace(":id", target.user_id)}
              target="_blank"
              rel="noreferrer"
              className="link link-primary"
            >
              {target.username || target.user_id}
            </a>
            {/* Keep the raw UUID visible — it is still what admins paste into the All Players search. */}
            <div className="text-gray-500 text-xs font-mono mt-1">{target.user_id}</div>
          </div>

          <div className="bg-base-200 dark:bg-[#0C0C1A] rounded-lg p-4">
            <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">{t("bonus2.spins.bonus")}</div>
            <div className="dark:text-white">{getDisplayName(target.bonus?.name, t("bonus2.shared.unknownBonus") as string)}</div>
            <div className="text-gray-500 text-xs">{target.bonus?.type}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-base-200 dark:bg-[#0C0C1A] rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-purple-500 dark:text-purple-400">{target.free_spins_awarded}</div>
              <div className="text-gray-500 dark:text-gray-400 text-sm">{t("bonus2.spins.awarded")}</div>
            </div>
            <div className="bg-base-200 dark:bg-[#0C0C1A] rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-orange-500 dark:text-orange-400">{target.free_spins_used}</div>
              <div className="text-gray-500 dark:text-gray-400 text-sm">{t("bonus2.spins.used")}</div>
            </div>
            <div className="bg-base-200 dark:bg-[#0C0C1A] rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-emerald-500 dark:text-emerald-400">{target.free_spins_remaining}</div>
              <div className="text-gray-500 dark:text-gray-400 text-sm">{t("bonus2.spins.remaining")}</div>
            </div>
            <div className="bg-base-200 dark:bg-[#0C0C1A] rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-yellow-500 dark:text-yellow-400">
                {formatFiatCurrency(Number(target.free_spins_winnings))}
              </div>
              <div className="text-gray-500 dark:text-gray-400 text-sm">{t("bonus2.spins.winnings")}</div>
            </div>
          </div>

          {target.free_spins_expires_at && (
            <div className="bg-base-200 dark:bg-[#0C0C1A] rounded-lg p-4">
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">{t("bonus2.spins.expiration")}</div>
              <div
                className={`font-semibold ${
                  isExpired(target.free_spins_expires_at)
                    ? "text-rose-500 dark:text-rose-400"
                    : isExpiringSoon(target.free_spins_expires_at)
                    ? "text-orange-500 dark:text-orange-400"
                    : "dark:text-white"
                }`}
              >
                {formatDateTime(target.free_spins_expires_at)}
                {isExpired(target.free_spins_expires_at) && " (EXPIRED)"}
              </div>
            </div>
          )}

          {target.bonus?.free_spins_game_ids && target.bonus.free_spins_game_ids.length > 0 && (
            <div className="bg-base-200 dark:bg-[#0C0C1A] rounded-lg p-4">
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">{t("bonus2.spins.eligibleGames")}</div>
              <div className="flex flex-wrap gap-2">
                {target.bonus.free_spins_game_ids.map((gameId, i) => (
                  <span key={i} className="px-2 py-1 bg-purple-900/30 text-purple-300 rounded text-xs font-mono">
                    {gameId}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* SOFT-736 — the stake this player actually spins at: the one FROZEN when the spins were
              awarded, in their own coin. The template's value (USD, and editable after the grant)
              is only shown when the row predates the freeze — and is labelled as the template's. */}
          {(target.free_spins_bet_value !== null && target.free_spins_bet_value !== undefined) || target.bonus?.free_spins_bet_value ? (
            <div className="bg-base-200 dark:bg-[#0C0C1A] rounded-lg p-4">
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">{t("bonus2.spins.betValuePerSpin")}</div>
              {target.free_spins_bet_value !== null && target.free_spins_bet_value !== undefined ? (
                <div className="dark:text-white font-semibold">{formatStake(target.free_spins_bet_value, target.coin)}</div>
              ) : (
                <div className="dark:text-white font-semibold">
                  {formatStake(target.bonus?.free_spins_bet_value, "USD")}
                  <span className="ml-2 text-xs font-normal text-gray-400">{t("bonus2.spins.fromTheBonusTemplate")}</span>
                </div>
              )}
            </div>
          ) : null}

          <div className="bg-base-200 dark:bg-[#0C0C1A] rounded-lg p-4">
            <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">{t("bonus2.spins.status")}</div>
            <span
              className={`badge ${
                target.status === "ACTIVE" ? "badge-success" : target.status === "COMPLETED" ? "badge-info" : "badge-ghost"
              }`}
            >
              {target.status}
            </span>
          </div>
        </div>

        <div className="modal-action">
          <button onClick={onClose} className="btn btn-secondary w-full">
            {t("bonus2.spins.close")}
          </button>
        </div>
      </div>
    </Modal>
  )
}
